// Libraries
import { CIICalculatorFactory, getVesselTypeFromString } from '@bearingai/cii-calculator-lib';
import { CIISimulator, DataRestAPIService, RoutingHistoricalDataService } from '@bearingai/cii-simulator';
import FOCRestService from '@bearingai/cii-simulator/dist/foc/FOCRestService';
import CIISimulationResult from '@bearingai/cii-simulator/dist/models/CIISimulationResult';
import { AxiosInstance } from 'axios';
// Utils
import { Rating } from '@utils/index';
// Types
import { RatingKeys } from '@globalTypes/index';
import { CIISimulatorState, DefaultParametersValues, SimulateParameters } from '@schemas/services';
import { CiiSimulatorSimulationData, CiiSimulatorStartData, OptimizedSpeedsAsNumber } from './SimulatorRunner.types';

// Optimized speed array form for operates with the lib results
const baseSpeeds = [
  {
    rating: 'A',
    ballastSpeedKts: -1,
    ladenSpeedKts: -1,
  },
  {
    rating: 'B',
    ballastSpeedKts: -1,
    ladenSpeedKts: -1,
  },
  {
    rating: 'C',
    ballastSpeedKts: -1,
    ladenSpeedKts: -1,
  },
  {
    rating: 'D',
    ballastSpeedKts: -1,
    ladenSpeedKts: -1,
  },
  {
    rating: 'E',
    ballastSpeedKts: -1,
    ladenSpeedKts: -1,
  },
];

/**
 * Class representing a simulator runner.
 *
 * @remarks
 * - This is a singleton class, meaning it will only ever instantiate one instance of itself.
 *   The same instance is returned every time getInstance() is called.
 */
export class SimulatorRunner {
  private static instance?: SimulatorRunner;
  customerId: number;
  apiGatewayInstance: AxiosInstance;
  dataService: DataRestAPIService;
  focService: FOCRestService;
  simulator?: CIISimulator;

  /**
   * Creates an instance of the simulator runner.
   *
   * @constructor
   * @param {number} customerId - The customer ID for whom the simulation is being set up.
   */
  constructor(customerId: number, apiGatewayInstance: AxiosInstance) {
    this.customerId = customerId;
    this.apiGatewayInstance = apiGatewayInstance;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.dataService = new DataRestAPIService(this.apiGatewayInstance as any);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    this.focService = new FOCRestService(this.apiGatewayInstance as any);
  }

  /**
   * Retrieves an instance of the SimulatorRunner for the provided customer ID.
   * If an instance doesn't exist, a new one is created.
   *
   * @method
   * @param customerId The customer's ID for which to get the SimulatorRunner instance.
   * @returns An instance of the SimulatorRunner.
   */
  public static getInstance(customerId: number, apiGatewayInstance: AxiosInstance): SimulatorRunner {
    if (!SimulatorRunner.instance) {
      SimulatorRunner.instance = new SimulatorRunner(customerId, apiGatewayInstance);
    }

    return SimulatorRunner.instance;
  }

  /**
   * Simulates the data for the provided state.
   *
   * @method
   * @param state The state parameters for the simulation.
   * @returns A promise containing the simulation data.
   */
  async simulate(state: SimulateParameters): Promise<CiiSimulatorSimulationData> {
    try {
      const simulationResult = (await this.simulator?.simulate(state)) as CIISimulationResult;
      const {
        simulatedCIIScores,
        yearToDateCIIScores,
        simulatedCIIRatingEndOfYear,
        bulkFuelIsGreater,
        bulkQuantityUsedMT,
        bulkUsageEndIndex,
      } = simulationResult;
      // yearToDateCIIScores[0].time is already a UTC time but is interpreted as:
      // new Date('2025-01-01 01:00:00') -> Wed Jan 01 2025 01:00:00 GMT-0500 (Colombia Standard Time)
      // We can grab the getFullYear as it will be 2025,
      // if we grabbed the getUTCFullYear it could change the actual year of the first report depending on the timezone
      const isCurrentYearData =
        yearToDateCIIScores.length > 0 &&
        new Date(yearToDateCIIScores[0].time).getFullYear() == new Date().getUTCFullYear();
      // If YTD is empty, the forecast rating is A and plot is empty
      const graphicsData = isCurrentYearData
        ? { simulatedCIIScores, yearToDateCIIScores, bulkUsageEndIndex }
        : { simulatedCIIScores: [], yearToDateCIIScores: [] };
      const forecastRating = isCurrentYearData ? (simulatedCIIRatingEndOfYear as RatingKeys) : Rating.A;
      const emissionsImpact = isCurrentYearData
        ? simulationResult.emissionsImpact
        : {
            fuelPricePerMT: 0,
            carbonCreditPricePerMT: 0,
            totalEmissionsMT: 0,
            mileageNM: 0,
          };

      return { emissionsImpact, graphicsData, forecastRating, bulkQuantityUsedMT, bulkFuelIsGreater };
    } catch (error) {
      throw new Error('Error simulating');
    }
  }

  /**
   * Gets the optimized speeds for the simulation parameters.
   *
   * @method
   * @param state The state parameters for the simulation.
   * @returns A promise containing the optimized speeds.
   */
  async getOptimizedSpeeds(state: SimulateParameters) {
    try {
      const optimizationPromises = Object.keys(Rating).map((rating) =>
        this.simulator?.optimizeForTargetRating(state, rating)
      );
      const optimizedSpeedsForRatings = await Promise.all(optimizationPromises);

      const optimizedSpeeds: OptimizedSpeedsAsNumber = optimizedSpeedsForRatings.reduce((acc, item, index) => {
        const base = baseSpeeds[index];

        return {
          ...acc,
          [base.rating]: {
            ballastSpeedKts: item?.ballastSpeedKts ?? base.ballastSpeedKts,
            ladenSpeedKts: item?.ladenSpeedKts ?? base.ladenSpeedKts,
          },
        };
      }, {} as OptimizedSpeedsAsNumber);

      return optimizedSpeeds;
    } catch (error) {
      throw new Error('Error getting the optimized speeds');
    }
  }

  /**
   * Starts the simulator with the provided IMO (International Maritime Organization) number.
   *
   * @method
   * @param imo The IMO number to use for starting the simulator.
   * @returns A promise containing the data required to start the simulator.
   */
  async startSimulator(imo: number): Promise<CiiSimulatorStartData> {
    try {
      const initialState = (await this.dataService.getInitialSimulatorState(
        String(this.customerId),
        imo
      )) as CIISimulatorState;

      const calculatorParams = {
        imo,
        type: getVesselTypeFromString(initialState.vessel.type),
        deadWeightTonnage: initialState.vessel.deadWeightTonnage,
        grossTonnage: initialState.vessel.grossTonnage,
        minSpeed: initialState.vessel.minSpeed,
        maxSpeed: initialState.vessel.maxSpeed,
        lastNoonReportDateUTC: new Date(initialState.vessel.lastNoonReportDateUTC),
      };

      const calculator = CIICalculatorFactory.create(calculatorParams);
      const routing = new RoutingHistoricalDataService(String(this.customerId), calculatorParams, this.dataService);
      await routing.fetchRoutingHistory();
      const simulator = new CIISimulator(calculatorParams, routing, calculator, this.focService, initialState);
      this.simulator = simulator;

      const { emissionsImpact, graphicsData, forecastRating } = await this.simulate(
        initialState.initialSimulationState
      );
      const {
        fuelTypes,
        initialSimulationState,
        parametersConfiguration: parametersRanges,
        underwaterCleaning,
        ciiRatingBoundaries,
        operationalYear,
      } = initialState;

      // If operationalYear is not the current year, the vessel has not sent any data this year
      const isCurrentYearData = new Date().getUTCFullYear() == operationalYear;
      const vessel = isCurrentYearData
        ? initialState.vessel
        : {
            ...initialState.vessel,
            yearToDateCiiRating: Rating.A,
            forecastedCiiRating: Rating.A,
            yearToDateCiiAttained: 0,
            forecastedCiiAttained: 0,
            requiredCiiScore: initialState.vessel.currentYearRequiredCiiScore,
          };

      const {
        primaryFuelTypeId,
        carbonCreditPricePerMT,
        ladenAverageSpeedKts,
        ballastAverageSpeedKts,
        averageDaysOfSailingPerMonth,
        averageBeaufort,
        useUnderWaterCleaning,
        fuelModes,
      } = initialSimulationState;

      const {
        yearToDateCiiAttained,
        forecastedCiiAttained,
        requiredCiiRating,
        yearToDateCiiRating,
        forecastedCiiRating,
        yearToDateDistanceSailed,
        forecastedDistanceSailed,
        requiredCiiScore,
        lastNoonReportDate,
      } = vessel;

      // Get ratings Per Distance
      const ratingsPerDistance = {
        yearToDateCiiRating: yearToDateCiiRating as RatingKeys,
        forecastedCiiRating: forecastedCiiRating as RatingKeys,
        requiredCiiRating: (requiredCiiRating as RatingKeys) ?? Rating.C,
        yearToDateCiiAttained,
        forecastedCiiAttained,
        requiredCiiScore,
        yearToDateDistanceSailed,
        forecastedDistanceSailed,
      };
      // Get fuelOptions
      const fuelOptions = fuelTypes.map((fuelType) => ({
        label: fuelType.displayName,
        value: fuelType.id,
        price: fuelType.price,
        co2ConversionFactor: fuelType.co2ConversionFactor,
        energyConversion: fuelType.energyConversion,
      }));
      const defaultFuelType =
        fuelOptions.find((fuelOption) => fuelOption.value === primaryFuelTypeId) ?? fuelOptions[0];

      // Get default parameters values
      const defaultParametersValues: DefaultParametersValues = {
        fuelType: defaultFuelType,
        bulkFuelType: null,
        bulkFuelPrice: 0,
        bulkFuelQuantity: 0,
        fuelPrice: defaultFuelType.price ?? 0,
        carbonPrice: carbonCreditPricePerMT ?? 100,
        ladenAvgSpeed: ladenAverageSpeedKts ?? 15,
        ballastAvgSpeed: ballastAverageSpeedKts ?? 15,
        sailingDays: averageDaysOfSailingPerMonth ?? 20,
        avgBeaufort: averageBeaufort ?? 3,
        uwc: useUnderWaterCleaning ?? false,
        fuelModes: fuelModes ?? [],
        bulkFuelEnabled: false,
      };

      // Get optimizedSpeeds
      const optimizedSpeeds = {
        A: {
          ballastSpeedKts: -1,
          ladenSpeedKts: -1,
        },
        B: {
          ballastSpeedKts: -1,
          ladenSpeedKts: -1,
        },
        C: {
          ballastSpeedKts: -1,
          ladenSpeedKts: -1,
        },
        D: {
          ballastSpeedKts: -1,
          ladenSpeedKts: -1,
        },
        E: {
          ballastSpeedKts: -1,
          ladenSpeedKts: -1,
        },
      };

      // Get UWC
      const uwc = {
        percentageChange: underwaterCleaning.underWaterCleaningPercentageChange * 100,
        co2Change: underwaterCleaning.underWaterCleaningCo2Change,
        focChange: underwaterCleaning.underWaterCleaningFocChange,
      };

      const currentRating = yearToDateCiiRating as RatingKeys;
      // TODO: this is a temporal solution
      const isValidData =
        graphicsData.simulatedCIIScores.length > 0 && isNaN(graphicsData.simulatedCIIScores[0].lowerCII);
      const forecastRatingDefinition = isValidData ? ratingsPerDistance.forecastedCiiRating : forecastRating;

      return {
        ciiRatingBoundaries,
        currentRating,
        requiredCiiScore,
        defaultParametersValues,
        emissionsImpact,
        forecastRating: isCurrentYearData ? forecastRatingDefinition : Rating.A,
        fuelOptions,
        graphicsData: isCurrentYearData ? graphicsData : { simulatedCIIScores: [], yearToDateCIIScores: [] },
        operationalYear,
        optimizedSpeeds,
        parametersRanges,
        ratingsPerDistance,
        uwc,
        lastNoonReportDate,
        bulkFuelIsGreater: false,
        bulkQuantityUsedMT: 0,
      };
    } catch (error) {
      throw new Error('Error creating the simulator');
    }
  }
}
